import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Global site-settings hook. Fetches admin-editable overrides for text, links,
 * images, and neighborhood snapshots. Cached on window for fast re-mount.
 * Falls back to {} on error so public pages always render defaults.
 */
const CACHE_KEY = "__tr_site_settings__";

export function useSiteSettings() {
  const [settings, setSettings] = useState(
    typeof window !== "undefined" ? window[CACHE_KEY] || {} : {}
  );
  const [loading, setLoading] = useState(!window?.[CACHE_KEY]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/site-settings");
      const data = res.data || {};
      setSettings(data);
      if (typeof window !== "undefined") window[CACHE_KEY] = data;
    } catch {
      setSettings({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!window?.[CACHE_KEY]) refresh();
  }, [refresh]);

  return { settings, loading, refresh };
}

/**
 * Resolve a media key returned by the admin upload endpoint into a fully-
 * qualified URL that <img src> can load. Falls back to the provided default
 * when no override key is set.
 */
export function mediaUrl(key, fallback) {
  if (!key) return fallback;
  if (key.startsWith("http://") || key.startsWith("https://")) return key;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/site-media/${key}`;
}

/** Small helper for text overrides. */
export function pickText(settings, path, fallback) {
  const parts = path.split(".");
  let cur = settings;
  for (const p of parts) {
    if (cur == null) return fallback;
    cur = cur[p];
  }
  return cur == null || cur === "" ? fallback : cur;
}
